const DATABASE_NAME = "lumen-tms-local";
const DATABASE_VERSION = 1;

export const STORES = { boardRows: "board_rows", meta: "meta", outbox: "outbox" };

let openPromise = null;

const canUseIndexedDb = () => typeof window !== "undefined" && "indexedDB" in window;

const openDatabase = () => {
  if (!canUseIndexedDb()) {
    return Promise.resolve(null);
  }
  if (!openPromise) {
    openPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORES.boardRows)) {
          database.createObjectStore(STORES.boardRows, { keyPath: "id" }).createIndex("load_id", "load_id", { unique: false });
        }
        if (!database.objectStoreNames.contains(STORES.meta)) {
          database.createObjectStore(STORES.meta, { keyPath: "key" });
        }
        if (!database.objectStoreNames.contains(STORES.outbox)) {
          database.createObjectStore(STORES.outbox, { keyPath: "id", autoIncrement: true });
        }
      };
      request.onerror = () => {
        openPromise = null;
        reject(request.error || new Error("Unable to open local database."));
      };
      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => database.close();
        resolve(database);
      };
    });
  }
  return openPromise;
};

const requestToPromise = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const runTransaction = async (storeNames, mode, work) => {
  const database = await openDatabase();
  if (!database) {
    return null;
  }
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeNames, mode);
    let result;
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error("Local database transaction aborted."));
    Promise.resolve(work((name) => transaction.objectStore(name)))
      .then((value) => { result = value; })
      .catch((error) => {
        try { transaction.abort(); } catch { /* no-op */ }
        reject(error);
      });
  });
};

export const localDb = {
  getAll: (storeName) => runTransaction(storeName, "readonly", (store) => requestToPromise(store(storeName).getAll())),
  get: (storeName, key) => runTransaction(storeName, "readonly", (store) => requestToPromise(store(storeName).get(key))),
  put: (storeName, value) => runTransaction(storeName, "readwrite", (store) => { store(storeName).put(value); }),
  add: (storeName, value) => runTransaction(storeName, "readwrite", (store) => requestToPromise(store(storeName).add(value))),
  delete: (storeName, key) => runTransaction(storeName, "readwrite", (store) => { store(storeName).delete(key); }),
  replaceAll: (storeName, values) => runTransaction(storeName, "readwrite", (store) => {
    const target = store(storeName);
    target.clear();
    values.forEach((value) => target.put(value));
  }),
  applyBoardLoadChanges: (loadChanges) => runTransaction(STORES.boardRows, "readwrite", async (store) => {
    const target = store(STORES.boardRows);
    const index = target.index("load_id");
    for (const { loadId, trips } of loadChanges) {
      const existingKeys = await requestToPromise(index.getAllKeys(loadId));
      existingKeys.forEach((key) => target.delete(key));
      trips.forEach((trip) => target.put(trip));
    }
  }),
  clearAll: () => runTransaction(Object.values(STORES), "readwrite", (store) => {
    Object.values(STORES).forEach((name) => store(name).clear());
  }),
};
